import type { EventSourceMessage } from '@microsoft/fetch-event-source';
import { notificationStatusReceived, invoiceUpdateReceived } from 'src/infra/stores/reducer/sseSlice';
import type { AppDispatch } from 'src/infra/stores/store';

/**
 * Handle SSE file status update events
 * Processes invoice file processing status updates and related notifications
 */
/**
 * Statuses that should trigger a notification (require user action)
 * These are final states or states that need user attention
 */
const NOTIFICATION_WORTHY_STATUSES = [
  'COMPLETE', // Processing done - user can review
  'REVIEW_REQUIRED', // Manual review needed
  'UNDER_REVIEW', // Being reviewed
  'OCR_ERROR', // Failed OCR
  'TRANSFORM_ERROR', // Failed transformation
  'MESSAGE_PROCESSING_FAILED', // Processing failed
  'PIPELINE_TRIGGER_FAILED', // Prefect pipeline trigger failed
  'DUPLICATE', // Duplicate invoice
  'INVALID_FILE', // Invalid file format
];

/**
 * Processing statuses that should NOT trigger notifications
 * User doesn't need to be notified about these intermediate states
 */
const PROCESSING_STATUSES = ['BEING_UPLOADED', 'UPLOADED_FILE', 'SENT_TO_PIPELINE', 'OCR_PROCESSING', 'OCR_COMPLETE', 'TRANSFORM_COMPLETE', 'LOADING_DATA'];

export const handleFileStatusUpdateEvent = (event: EventSourceMessage, dispatch: AppDispatch): void => {
  try {
    const data = JSON.parse(event.data);
    console.log('📊 SSE: fileStatusUpdate', data);

    // Handle invoice processing updates first
    if (data.invoiceFileProcessingStatus) {
      dispatch(invoiceUpdateReceived(data.invoiceFileProcessingStatus));
    }

    // Handle notification updates with filtering
    if (data.notification) {
      const status = data.invoiceFileProcessingStatus?.status;

      // ✅ Only show notification for statuses that require user action
      const shouldNotify = !status || NOTIFICATION_WORTHY_STATUSES.includes(status);

      if (shouldNotify) {
        dispatch(
          notificationStatusReceived({
            unreadCount: data.notification.unreadCount ?? 1,
            lastUpdated: data.notification.lastUpdated ?? new Date().toISOString(),
          })
        );
      } else {
        console.log(`ℹ️ SSE: Skipping notification for processing status: ${status}`);
      }
    }
  } catch (error) {
    console.error('❌ SSE: Error parsing fileStatusUpdate event:', error);
  }
};
